import { useCallback, useEffect, useRef, useState } from "react";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import { DataPermissions } from "../../../../classes/enums/PermissionsEnum";
import { formatDisplayDate } from "../../../../classes/utils/DateUtils";
import AddNew from "../../../../components/addNew/AddNew";
import { ErrorMessage } from "../../../../components/errorMessage/ErrorMessage";
import FilterAndActionBar from "../../../../components/filterAndActionBar.js/FilterAndActionBar";
import MovablePopup from "../../../../components/popup/MovablePopup";
import WidgetLoader from "../../../../components/widgetLoader/WidgetLoader";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useAuth } from "../../../../hooks/useAuth";
import { useRouteState } from "../../../../hooks/useRouteState";
import FileImport from "../../../common/uploadModal/fileImport/FileImport";
import "../common/PatientWidgetLayout.css";
import usePatientWidgetControlsOffset from "../common/usePatientWidgetControlsOffset";
import MorphologyGallery from "./MorphologyGallery";
import "./PatientMorphology.css";

const PatientMorphology = () => {
  const { t } = useTranslation();
  const { showPopup } = useAppContext();
  const { checkPermissions } = useAuth();
  const { dateSettings } = useUser();
  const {
    patientImages,
    fetchPatientImages,
    filterPatientImagesByDate,
    searchPatientImages,
    searchLoading,
    searchError,
  } = usePatientContext();
  const { params } = useRouteState();

  const [filteredImages, setFilteredImages] = useState([]);
  const [filteredImagesPatientId, setFilteredImagesPatientId] = useState(null);
  const patientIdRef = useRef(params.patientId);
  patientIdRef.current = params.patientId;
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [openWindows, setOpenWindows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const canImport = checkPermissions(DataPermissions.IMPORT_MORPHOLOGY_IMAGE);
  const canDelete = checkPermissions(DataPermissions.DELETE_MORPHOLOGY_IMAGE);

  // Handle patient change
  useEffect(() => {
    if (!params.patientId) return;

    const controller = new AbortController();
    setFilteredImages([]);
    setFilteredImagesPatientId(null);
    setError(null);
    setOpenWindows([]);
    setIsLoading(true);

    fetchPatientImages(controller.signal)
      .catch(error => {
        if (!controller.signal.aborted)
          setError({ message: "No images found" });
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [params.patientId, fetchPatientImages]);

  const applyFilters = useCallback(async () => {
    const currentPatientId = patientIdRef.current;
    if (!patientImages?.length) {
      setFilteredImages([]);
      setFilteredImagesPatientId(currentPatientId);
      return;
    }

    setIsLoading(true);
    try {
      let result = patientImages;

      if (dateRange.start || dateRange.end) {
        result = filterPatientImagesByDate(
          dateRange.start,
          dateRange.end,
          result
        );
      }

      if (searchTerm) {
        result = searchPatientImages(searchTerm, result);
      }

      setFilteredImages(result?.length ? result : []);
      setFilteredImagesPatientId(currentPatientId);
    } catch (error) {
      setError(error);
      setFilteredImages([]);
      setFilteredImagesPatientId(currentPatientId);
    } finally {
      setIsLoading(false);
    }
  }, [
    patientImages,
    dateRange,
    searchTerm,
    filterPatientImagesByDate,
    searchPatientImages,
  ]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSearch = term => {
    setSearchTerm(term);
  };

  const handleDateRangeChange = (start, end) => {
    setDateRange({ start, end });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDateRange({ start: null, end: null });
  };

  const handleImageClick = image => {
    if (!image?.objectUrl) return;

    const formattedDate =
      formatDisplayDate(image.date, {
        locale: dateSettings?.locale,
        format: dateSettings?.format,
      }) || "-";

    // Extract original filename from decorated name: {originalName}_{modality}_{orientation}_{acquisitionDate}_{currentDate}.ext
    const fullName = image.title || "";
    const extMatch = fullName.match(/(\.[^.]+)$/);
    const ext = extMatch ? extMatch[1] : "";
    const displayTitle = fullName.split("_")[0] + ext;

    const newWindow = {
      id: Date.now(),
      image: { ...image, formattedDate, displayTitle },
    };
    setOpenWindows(prevWindows => [...prevWindows, newWindow]);
  };

  const handleImageLoad = (event, windowId) => {
    const img = event.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;

    setOpenWindows(prevWindows =>
      prevWindows.map(window =>
        window.id === windowId ? { ...window, aspectRatio } : window
      )
    );
  };

  const handleCloseWindow = id => {
    setOpenWindows(prevWindows =>
      prevWindows.filter(window => window.id !== id)
    );
  };

  const { controlsRef, shellStyle } = usePatientWidgetControlsOffset();

  const openFileUploadModal = () => {
    showPopup(
      <FileImport
        onSave={() => {}}
        allowedFileTypes={[".jpg", ".jpeg", ".png", ".bmp", ".heic"]}
      />
    );
  };
  const renderGalleryContent = () => {
    if (
      isLoading ||
      searchLoading ||
      filteredImagesPatientId !== params.patientId
    ) {
      return (
        <div>
          <WidgetLoader isLoading={true} />
        </div>
      );
    }

    if (error || searchError) {
      return <ErrorMessage message={t("91")} />;
    }

    if (!filteredImages || filteredImages.length === 0) {
      return (
        <AddNew
          elementTitle="Morphology Image"
          onClick={canImport ? openFileUploadModal : null}
        />
      );
    }

    return (
      <MorphologyGallery
        images={filteredImages}
        onImageClick={handleImageClick}
        canDelete={canDelete}
        patientId={params.patientId}
        onImageDeleted={fetchPatientImages}
      />
    );
  };

  return (
    <div
      className="patient-morphology-container patient-widget-shell"
      key={params.patientId}
      style={shellStyle}
    >
      <div className="gallery-container patient-widget-content">
        {renderGalleryContent()}
      </div>

      <div
        className="patient-morphology-controls patient-widget-controls"
        ref={controlsRef}
      >
        <FilterAndActionBar
          onSearch={handleSearch}
          onDateRangeChange={handleDateRangeChange}
          onClear={clearFilters}
          onAdd={canImport ? openFileUploadModal : null}
          value={searchTerm}
        />
      </div>
      {openWindows.map((window, index) => (
        <MovablePopup
          key={window.id}
          index={index}
          title={window.image.displayTitle}
          subtitle={window.image.formattedDate}
          onClose={() => handleCloseWindow(window.id)}
          initialPos={{ x: 10 + index * 300, y: 25 }}
        >
          <img
            src={window.image.objectUrl}
            alt={window.image.title}
            onLoad={e => handleImageLoad(e, window.id)}
            className={window.aspectRatio > 1 ? "landscape" : "portrait"}
          />
        </MovablePopup>
      ))}
    </div>
  );
};

export default PatientMorphology;
